import axios, { AxiosHeaders, type InternalAxiosRequestConfig } from 'axios'
import type { QueryClient } from '@tanstack/react-query'
import { ensureWorkspaceToken } from './workspaceJwt'

/**
 * apiGateway — dedicated axios client for ADR-024 β-prime workspace-scoped
 * calls. Targets the public api-gateway (`/api/v1/...`), separate from the
 * admin-service client in `api.ts`.
 *
 * Per-request, the caller MUST attach a `workspaceId` to the axios config.
 * The request interceptor uses `ensureWorkspaceToken` (TanStack Query's
 * `ensureQueryData`) to await a fresh JWT, then sets the `Authorization`
 * header. Concurrent requests during a token refresh share the same
 * in-flight mint instead of stampeding.
 *
 * Example consumer (analyticsApi.ts):
 *   apiGateway.get<OverviewMetrics>(`/workspaces/${wid}/analytics/overview`,
 *     { workspaceId: wid, params: { startDate, endDate } })
 */

const API_GATEWAY_BASE_URL = import.meta.env.VITE_API_GATEWAY_URL || '/api/v1'

export const apiGateway = axios.create({
  baseURL: API_GATEWAY_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// QueryClient reference is registered by main.tsx after creation to avoid
// a circular import with the React tree. Matches the `registerAuthAccessors`
// pattern already used by api.ts for the admin-service client.
let _queryClient: QueryClient | null = null

export function registerApiGatewayQueryClient(queryClient: QueryClient): void {
  _queryClient = queryClient
}

// Augment AxiosRequestConfig to allow `workspaceId` as a typed per-request
// option without an `as any` cast at every call site.
declare module 'axios' {
  interface InternalAxiosRequestConfig {
    workspaceId?: string
  }
  interface AxiosRequestConfig {
    workspaceId?: string
  }
}

apiGateway.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const workspaceId = config.workspaceId

    if (!workspaceId) {
      return config
    }

    if (!_queryClient) {
      throw new Error(
        '[apiGateway] QueryClient not registered. Call ' +
          'registerApiGatewayQueryClient(queryClient) in main.tsx before ' +
          'making workspace-scoped requests.'
      )
    }

    const { token } = await ensureWorkspaceToken(_queryClient, workspaceId)
    const headers = config.headers ?? new AxiosHeaders()
    if (headers instanceof AxiosHeaders) {
      headers.set('Authorization', `Bearer ${token}`)
      config.headers = headers
    } else {
      const wrapped = new AxiosHeaders(headers as Record<string, string>)
      wrapped.set('Authorization', `Bearer ${token}`)
      config.headers = wrapped
    }

    return config
  }
)
